// Swipe State Management Hook
// Custom hook for managing swipe interface state and actions

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  SwipeState, 
  SwipeCard, 
  SwipeDirection, 
  SwipeAction, 
  SwipeSettings, 
  SwipeAnalytics,
  UserPreferences,
  UseSwipeReturn,
  SwipeGestureHandlers
} from '../../types/swipe';
import { logger } from '../../utils/logger';

// Default settings
const defaultSettings: SwipeSettings = {
  enableHaptics: true,
  enableSounds: true,
  swipeThreshold: 50,
  flipAnimationDuration: 600,
  swipeAnimationDuration: 300,
  maxCardsPerDay: 50,
  enableUndo: true,
  undoTimeLimit: 10
};

// Default analytics
const defaultAnalytics: SwipeAnalytics = {
  cardsViewed: 0,
  cardsDiscarded: 0,
  cardsSkipped: 0,
  cardsKept: 0,
  cardsPlayed: 0,
  matchRate: 0,
  avgResponseTime: 0,
  cardsSwiped: 0,
  cardsFlipped: 0,
  likesGiven: 0,
  passesGiven: 0,
  superLikesGiven: 0,
  savesGiven: 0,
  averageSessionDuration: 0,
  lastSessionDate: new Date()
};

export const useSwipe = (initialCards: SwipeCard[] = []): UseSwipeReturn => {
  // State
  const [state, setState] = useState<SwipeState>({
    cards: initialCards,
    currentCardIndex: 0,
    isAnimating: false,
    settings: defaultSettings,
    analytics: defaultAnalytics,
    isLoading: false,
    error: null
  });

  // Refs
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSwipeRef = useRef<{ cardId: string; direction: SwipeDirection; timestamp: Date } | null>(null);
  const sessionStartRef = useRef<Date>(new Date());

  // Haptic feedback
  const triggerHaptic = useCallback((type: 'light' | 'medium' | 'heavy' = 'light') => {
    if (!state.settings.enableHaptics || !navigator.vibrate) return;
    
    switch (type) {
      case 'light':
        navigator.vibrate(50);
        break;
      case 'medium':
        navigator.vibrate(100);
        break;
      case 'heavy':
        navigator.vibrate([100, 50, 100]);
        break;
    }
  }, [state.settings.enableHaptics]);

  // Sound feedback
  const triggerSound = useCallback((type: 'flip' | 'swipe' | 'match' | 'error') => {
    if (!state.settings.enableSounds) return;
    
    // In a real implementation, you would play actual sound files
    logger.log(`Playing sound: ${type}`);
  }, [state.settings.enableSounds]);

  // Update analytics
  const updateAnalytics = useCallback((action: SwipeAction) => {
    setState(prev => ({
      ...prev,
      analytics: {
        ...prev.analytics,
        cardsSwiped: (prev.analytics.cardsSwiped || 0) + 1,
        [`${action}sGiven`]: (prev.analytics[`${action}sGiven` as keyof SwipeAnalytics] as number) + 1,
        lastSessionDate: new Date()
      }
    }));
  }, []);

  // Swipe card
  const swipeCard = useCallback((direction: SwipeDirection) => {
    const currentCard = state.cards[state.currentCardIndex];
    if (!currentCard || state.isAnimating) return;

    // Set animation state
    setState(prev => ({
      ...prev,
      isAnimating: true,
      cards: prev.cards.map((card, index) => 
        index === state.currentCardIndex 
          ? { ...card, swipeDirection: direction, isAnimating: true }
          : card
      )
    }));

    // Determine action based on direction
    let action: SwipeAction;
    switch (direction) {
      case 'left':
        action = 'pass';
        break;
      case 'right':
        action = 'like';
        break;
      case 'up':
        action = 'superlike';
        break;
      case 'down':
        action = 'save';
        break;
    }

    // Store for undo
    lastSwipeRef.current = {
      cardId: currentCard.id,
      direction,
      timestamp: new Date()
    };

    // Update analytics
    updateAnalytics(action);

    // Trigger feedback
    triggerHaptic('medium');
    triggerSound('swipe');

    // Set undo timeout
    if (state.settings.enableUndo) {
      undoTimeoutRef.current = setTimeout(() => {
        lastSwipeRef.current = null;
      }, (state.settings.undoTimeLimit || 10) * 1000);
    }

    // Move to next card after animation
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        currentCardIndex: prev.currentCardIndex + 1,
        isAnimating: false
      }));
    }, state.settings.swipeAnimationDuration);
  }, [state.cards, state.currentCardIndex, state.isAnimating, state.settings, updateAnalytics, triggerHaptic, triggerSound]);

  // Flip card
  const flipCard = useCallback(() => {
    const currentCard = state.cards[state.currentCardIndex];
    if (!currentCard || state.isAnimating) return;

    setState(prev => ({
      ...prev,
      isAnimating: true,
      cards: prev.cards.map((card, index) => 
        index === state.currentCardIndex 
          ? { ...card, isFlipped: !card.isFlipped, isAnimating: true }
          : card
      ),
      analytics: {
        ...prev.analytics,
        cardsFlipped: (prev.analytics.cardsFlipped || 0) + 1
      }
    }));

    // Trigger feedback
    triggerHaptic('light');
    triggerSound('flip');

    // Reset animation state
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        isAnimating: false
      }));
    }, state.settings.flipAnimationDuration);
  }, [state.cards, state.currentCardIndex, state.isAnimating, state.settings, triggerHaptic, triggerSound]);

  // Undo last swipe
  const undoLastSwipe = useCallback(() => {
    if (!lastSwipeRef.current || state.isAnimating) return false;

    // Clear undo timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }

    // Move back to previous card
    setState(prev => ({
      ...prev,
      currentCardIndex: Math.max(0, prev.currentCardIndex - 1),
      cards: prev.cards.map((card, index) => 
        index === prev.currentCardIndex - 1 
          ? { ...card, swipeDirection: undefined, isAnimating: false }
          : card
      )
    }));

    // Reset last swipe
    lastSwipeRef.current = null;

    // Trigger feedback
    triggerHaptic('light');

    return true;
  }, [state.isAnimating, triggerHaptic]);

  // Load more cards
  const loadMoreCards = useCallback(async (newCards: SwipeCard[]) => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setState(prev => ({
        ...prev,
        cards: [...prev.cards, ...newCards],
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load cards',
        isLoading: false
      }));
    }
  }, []);

  // Update settings
  const updateSettings = useCallback((newSettings: SwipeSettings) => {
    setState(prev => ({
      ...prev,
      settings: { ...prev.settings, ...newSettings }
    }));
  }, []);

  // Gesture handlers
  const handlers: SwipeGestureHandlers = {
    onSwipeLeft: useCallback((cardId: string) => {
      swipeCard('left');
    }, [swipeCard]),
    
    onSwipeRight: useCallback((cardId: string) => {
      swipeCard('right');
    }, [swipeCard]),
    
    onSwipeUp: useCallback((cardId: string) => {
      swipeCard('up');
    }, [swipeCard]),
    
    onSwipeDown: useCallback((cardId: string) => {
      swipeCard('down');
    }, [swipeCard]),
    
    onTap: useCallback((cardId: string) => {
      flipCard();
    }, [flipCard]),
    
    onLongPress: useCallback((cardId: string) => {
      // Long press functionality (e.g., quick preview)
      triggerHaptic('light');
    }, [triggerHaptic])
  };

  // Actions
  const actions = {
    swipeCard,
    flipCard,
    undoLastSwipe,
    loadMoreCards,
    updateSettings
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  // Update session duration
  useEffect(() => {
    const interval = setInterval(() => {
      const sessionDuration = Date.now() - sessionStartRef.current.getTime();
      setState(prev => ({
        ...prev,
        analytics: {
          ...prev.analytics,
          averageSessionDuration: sessionDuration
        }
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return {
    state,
    actions,
    handlers
  };
};
